import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
export function ThesisPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-navy text-white/90 font-sans selection:bg-violet-electric/30" data-id="element-942">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden" data-id="element-943">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-944">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.15) 0%, rgba(200, 80, 192, 0.05) 40%, transparent 70%)'
        }} data-id="element-945" />

        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-946">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-947">

            <span className="text-violet-electric text-sm font-semibold tracking-widest uppercase mb-6 block" data-id="element-948">
              Our Thesis
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/70" data-id="element-949">
              The ERP Factory: The Next Era of Enterprise Software Won't Be
              Implemented — It Will Be Manufactured
            </h1>
            <div className="flex items-center justify-center gap-4 text-white/50 text-sm font-medium" data-id="element-950">
              <span data-id="element-951">February 2026</span>
              <span className="w-1 h-1 rounded-full bg-white/30" data-id="element-952" />
              <span data-id="element-953"></span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <article className="relative z-10 max-w-3xl mx-auto px-6 pb-32" data-id="element-954">
        <div className="space-y-6" data-id="element-955">
          <p className="text-xl text-white/90 font-medium leading-relaxed" data-id="element-956">
            Something is happening in software development that most of the
            enterprise world hasn't noticed yet.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-957">
            A small team at{' '}
            <a href="https://simonwillison.net/2026/Feb/7/software-factory/" target="_blank" rel="noopener noreferrer" className="text-violet-electric hover:text-violet-electric/80 transition-colors underline" data-id="element-958">

              StrongDM
            </a>{' '}
            just published a manifesto about what they call the Software
            Factory. Three engineers. No hand-coded software. No human code
            review. They feed specifications and scenarios into a system of
            agents that write code, validate it against behavioral clones of
            real-world services, and iterate until the output converges. Humans
            define intent. Machines execute.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-959">
            They're not alone. Across software companies teams are arriving at
            the same conclusion from different starting points. The factory
            model — where humans specify and machines produce — is becoming
            consensus among people building software at the agentic AI frontier.
            This seems to be a culmination of the progression of automating code
            production from the development of CI/CD, through the push for
            infrastructure-as-code and now agentic coding workflows. The
            trajectory is clear: software development is moving from an artisan
            job to an automated factory.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-960">
            But there's a massive category of enterprise software that this
            factory revolution hasn't touched. A category where every single
            deployment is still a bespoke, handcrafted, months-long artisan
            project. Where senior consultants fly to client sites, sit in
            conference rooms for weeks, and slowly translate institutional
            knowledge into system configurations that no one will fully
            understand six months later.
          </p>

          <p className="text-white font-bold text-lg leading-relaxed" data-id="element-961">ERP.</p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-962">
            Enterprise resource planning — the operational backbone of every
            physical business on the planet — is still stuck in pre-industrial
            mode. And the reason it's stuck has nothing to do with the software
            itself, it's stuck because ERP implementation has a problem that
            software development doesn't: the spec isn't technical. It's
            operational. The "requirements" for an ERP system aren't features to
            be built. They're business processes to be understood, captured, and
            encoded. That understanding has always been trapped inside discovery
            meetings, consultant notebooks, and the heads of people who've been
            doing the job for twenty years.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-963">
            ERP implementations still operate like a craft guild. A senior
            consultant apprentices alongside the client's team for weeks,
            produces hand-drawn process maps, and translates tribal knowledge
            into system configuration. It's artisan work and produces
            one-of-a-kind results. It's also extraordinarily expensive, slow,
            and fragile because when that consultant walks away, so does the
            understanding of why anything was built the way it was.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-964">
            The ERP Factory is the thesis that this era is ending. At Thruline,
            it's what we're building.
          </p>

          <hr className="border-white/10 my-16" data-id="element-965" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-966">
            What an ERP Factory Requires
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-967">
            At Thruline we believe the following has to be true to construct an
            ERP Factory:
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-968">
            Machine-readable requirements from primary sources.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-969">
            The factory can't start with a whiteboard. The most expensive
            meeting in business is the one where a consultant asks you to
            describe your own job. A company that's been running its operations
            for years has already produced an accurate requirements document —
            it just doesn't look like one. It looks like emails to vendors,
            purchase orders in spreadsheets, inspection checklists as PDFs,
            shipping notifications in inboxes, and SOPs buried in shared drives.
            The evidence of how the business actually works flows through the
            organization every single day, in every document the team produces.
            To unlock building an ERP Factory discovery can't be a human
            bottleneck, it has to be an ingestion pipeline.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-970">
            A traceable link between requirements and configuration.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-971">
            In a factory, every output traces back to its specification. If you
            can't explain why a configuration exists by pointing to the
            requirement that created it, you don't have a factory. You have a
            craft shop with automation bolted on.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-972">
            A traditional ERP implementation produces a configured system, and
            the "why" behind each configuration lives in a consultant's head, a
            stale Confluence page/Excel Sheet, or nowhere at all. When
            requirements change six months later nobody knows what to touch
            without risking cascading failures and the person who understood the
            original logic has moved on to the next engagement. The
            documentation, if it ever existed, hasn't been updated since
            go-live. The system works, but no one can explain it, and no one
            trusts themselves to change it.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-973">
            Linking every ERP configuration to a specific requirement is the
            difference between a system that was built fast and a system that
            can be maintained, evolved, and understood over time.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-974">
            Expert review as quality control, not as the production process.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-975">
            The ERP factory doesn't eliminate human expertise, it repositions
            it. In manufacturing, humans don't stand on the line assembling
            every unit. They design the process, inspect the output, and
            intervene when something's wrong. The same must be true for the new
            age of ERP systems. Consultants should be diagnosing and correcting,
            not spending weeks discovering and documenting information that
            already exists in the client's own files.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-976">
            Automated documentation and knowledge propagation.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-977">
            A factory doesn't just produce a product. It produces the
            documentation, training materials, and traceability records that
            make the product maintainable. An ERP that ships without the
            organization understanding why it works the way it does isn't a
            finished product. It's a time bomb with a six-month fuse.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-978">
            A feedback loop that compounds.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-979">
            Every unit the factory produces should make the next one better.
            Implementation knowledge — what works, what fails, what best
            practices look like in specific industries — must accumulate in the
            system, not in individual consultants' heads. The factory gets
            smarter with every engagement. The craft guild loses knowledge every
            time a consultant leaves.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-980">
            These aren't aspirational principles. They're engineering
            requirements. And until very recently, they were impossible to
            satisfy, because the tools to produce the requirements at scale
            didn't exist.
          </p>

          <hr className="border-white/10 my-16" data-id="element-981" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-982">
            Why Now: The Ingestion Breakthrough
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-983">
            The Software Factory movement was arguably catalyzed with the second
            revision of Claude 3.5. Long-horizon agentic coding workflows began
            to compound correctness rather than error. By December 2025 the
            signal was unmistakable: non-interactive development — software
            grown from specifications rather than hand-coded — went from an
            experiment to production capable.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-984">
            The ERP Factory is catalyzed by a parallel but different threshold:
            AI models that can reliably reconstruct operational processes from
            unstructured documents. Not summarization. Not keyword extraction.
            Process reconstruction — inferring workflows, roles, handoffs,
            decision points, and data dependencies from the messy documentation
            trail that every business leaves behind.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-985">
            Consider what a company's documents actually contain. The emails
            show who communicates with whom, in what order, about what
            decisions. The spreadsheets reveal what data the team tracks, what
            formulas they apply, what columns matter enough to maintain by hand.
            The PDFs, packing slips, inspection checklists, purchase orders,
            receiving reports — show what information travels between steps,
            what format it takes, and what details the business considers
            important enough to print on paper. Taken individually, these are
            just files. Taken together, they are a process map the company has
            been drawing unconsciously every single day it has been in
            operation.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-986">
            The consultants who do ERP implementation know this intuitively.
            Every experienced consultant has had the moment where they realize
            the client's spreadsheet tells them more about the actual process
            than the last four meetings combined. The meetings produced
            aspirational descriptions of how things should work — the
            spreadsheet shows how things actually work. The gap between those
            two things is where most ERP implementations go wrong.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-987">
            What's changed is that AI models can now do what that consultant
            does, but at machine scale, across every document the client
            produces, in hours rather than weeks. Six months ago, extracting
            coherent process understanding from a pile of unstructured
            operational documents wasn't reliable enough to build a production
            system on. Now it is. That's why the ERP Factory is possible now in
            a way it wasn't before.
          </p>

          <hr className="border-white/10 my-16" data-id="element-988" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-989">
            The Consultant in the Factory
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-990">
            There's a version of this story where I tell you we've automated
            away the need for ERP consultants. That's not this story.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-991">
            The factory didn't eliminate the machinist. It made the machinist's
            expertise the most valuable thing on the floor. Before factories, a
            machinist spent most of their time on setup, measurement, material
            handling, work that required skill but not their particular skill.
            The factory automated the repetitive production work and let the
            machinist focus on what only they could do: diagnosing problems,
            setting tolerances, inspecting output, and making the judgment calls
            that no machine could make.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-992">
            That's exactly what happens to the ERP consultant in the factory
            model.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-993">
            In the traditional model, a senior ERP consultant (someone with ten
            or fifteen years of implementation experience) spends maybe 20% of
            their time doing work that genuinely requires that expertise. The
            diagnostic work. The moment where they look at a client's process
            and see, instantly, that the workaround the team has been using for
            years is actually a critical vulnerability. The prescriptive work,
            where they know from experience what the right process looks like
            because they've implemented it a dozen times at similar companies —
            that's the 20% that justifies their rate.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-994">
            The other 80% is discovery meetings, documentation, project
            management, status updates, data migration coordination, and
            administrative overhead. It's necessary work, but it's not their
            work.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-995">
            In the factory model, the machine handles the 80%, the consultant
            focuses entirely on the 20%, but now that 20% is all they do, every
            hour of every day. They're diagnosing process gaps. They're
            correcting requirements that the system faithfully reconstructed
            from documents but that reveal operational weaknesses the client
            didn't know they had. They're validating that the configured system
            actually reflects operational reality. They're doing the work that
            takes years of experience to do well.
          </p>

          <hr className="border-white/10 my-16" data-id="element-996" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-997">
            The Compounding Factory
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-998">
            Every factory gets better with volume, and the ERP Factory is no
            exception.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-999">
            Once a factory system processes real operational documents across
            multiple clients in similar industries, its understanding of
            best-in-class processes deepens with every engagement. When we
            ingest a new manufacturing client's receiving documents, we're not
            just reading their process. We're comparing it — automatically,
            structurally — against every receiving process we've built and every
            consultant correction we've made across dozens of similar
            implementations.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1000">
            This means the 24-hour implementation doesn't just replicate what
            the client is already doing. It illuminates. It shows where the
            client's processes diverge from what works well elsewhere. It
            identifies gaps that companies in the same industry commonly have.
            It surfaces strengths — places where the client has actually
            developed a better approach than the norm. The ERP becomes an
            advisor, not just a record-keeper.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1001">
            The consultant corrections compound too. When a consultant
            identifies a vulnerability in one client's procurement process —
            say, a lack of three-way matching between POs, receipts, and
            invoices — that knowledge becomes part of the factory's baseline for
            the next similar client. The system learns to look for it. The next
            consultant reviewing a similar requirements set sees the issue
            flagged before they even start their review.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1002">
            This is the structural advantage an ERP Factory has over traditional
            consulting firms. A consulting firm's knowledge lives in its people.
            When a senior consultant retires or moves to a competitor, decades
            of implementation knowledge walk out the door. Client lessons,
            industry patterns, hard-won insights about what works and what
            doesn't — all of it is personally held, imperfectly documented, and
            non-transferable. The firm doesn't get smarter over time. Individual
            consultants get smarter, and the firm rents their knowledge until
            they leave.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1003">
            The ERP Factory is still a system of record, but now it's a full
            record of every configuration that was changed and why. There are
            fully documented process gaps with industry-specific edge cases, and
            the solutions that worked. It doesn't forget. It doesn't leave. It
            compounds. Every implementation makes the next one faster, more
            accurate, and more valuable.
          </p>

          <hr className="border-white/10 my-16" data-id="element-1004" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-1005">
            The Economics of the ERP Factory
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1006">
            The speed of AI change is bringing about "unconventional economics"
            an observation that things which were always technically possible
            but economically unthinkable are now routine. In the software
            factory analogy, building a full behavioral clone of Okta or other
            3rd party apps purely for unit testing was always possible. No
            engineering team would ever have proposed it, because they knew the
            answer would be no because the economics didn't work. Now they do.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1007">
            The same unconventional economics apply to ERP, but the magnitude is
            larger.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1008">
            A traditional ERP implementation for a mid-market manufacturer costs
            $150,000 to $500,000 and takes three to nine months. That cost is
            almost entirely labor: senior consultants billing at $200-$350 per
            hour, flying to client sites, sitting in meetings, writing
            documents, configuring systems, troubleshooting integrations, and
            conducting training. The typical consulting firm generates $300,000
            to $500,000 in revenue per consultant, at services margins of
            15-25%.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1009">
            In the factory model, the cost structure inverts. The ingestion,
            requirements construction, configuration, and documentation
            processes are automated. The consultant's time is focused on the
            high-value diagnostic and corrective work — hours instead of weeks.
            The production cost of an implementation drops dramatically while
            the value delivered actually increases, because the client receives
            not just a configured system but a fully traced, self-documenting
            knowledge layer.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1010">
            This allows an ERP Factory to price on value, not hours. The
            deliverable is worth the same to the client whether it takes 24
            hours or six months to produce — arguably more, because they get it
            faster and it comes with better documentation and traceability than
            any traditional implementation could provide.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1011">
            The unit economics are what make this a fundamentally different
            business, not just a faster version of the old one. At Thruline
            we're targeting $1 million in revenue per consultant — two to three
            times the industry standard, at software margins rather than
            services margins. The consultant isn't working more hours, they're
            working on the things that only they can do, while the factory
            handles everything else.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1012">
            This creates a compounding economic engine: high-margin
            implementation revenue upfront, sticky recurring licensing revenue
            underneath, and a cost structure that improves with every new
            client. Artisan pricing for manufactured goods — that's how you
            build a category.
          </p>

          <hr className="border-white/10 my-16" data-id="element-1013" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-1014">
            The Thruline Implementation: An ERP Factory in Practice
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1015">
            At Thruline we didn't set out to build a factory. We set out to stop
            wasting our consultants' time.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1016">
            My co-founders and I are all former software engineers turned ERP
            implementation consultants. We've spent weeks of discovery that
            produced requirements documents nobody fully trusted. Configurations
            that couldn't be traced back to business logic three months later
            and documentation that went stale before it was released.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1017">
            We followed that process for our current clients, and it's tedious.
            What we've been working on is the ability to structurally ingest our
            customer's documentation and pull out the requirements we've
            previously manually created. What we've been seeing in the last 6
            months is that the implementation model has changed. Not
            incrementally. Structurally.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1018">
            Here's the ERP Factory we're building at Thruline:
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-1019">
            Ingestion.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1020">
            The client gives us what they already have. Emails. Spreadsheets.
            PDFs. SOPs, if they exist. Tribal knowledge captured in whatever
            messy, inconsistent, unorganized format it lives in. We don't ask
            them to clean it up. We don't ask them to translate it into our
            language or organize it into categories. We take it raw. The whole
            point is to meet the company where it actually operates — in its
            real documents, produced in the course of real work — not where a
            consultant wishes it operated.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-1021">
            Requirements Construction.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1022">
            Our system reads those documents and constructs a structured
            requirements map. It's not pattern-matching keywords, it's
            understanding what the documents reveal about the actual operational
            workflow. Who sends what to whom. What data is being tracked and
            why. Where decisions get made. What the exceptions look like and how
            they're currently handled. We cross-reference all of this against
            our accumulated knowledge of how these processes should work across
            the client's industry.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1023">
            What comes out of this phase is a first-pass requirements set that
            is remarkably close to what a seasoned consultant would produce
            after weeks of discovery. Generated in hours, from documents the
            client already had sitting in their inboxes and shared drives.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-1024">
            Consultant Quality Control.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1025">
            This is where our approach diverges from the fully automated fantasy
            that most AI companies are selling right now. We don't hand the
            machine-generated requirements straight to the configuration engine,
            our consultants review every requirement the system produces.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1026">
            And this is where the ERP factory model becomes genuinely powerful,
            because the consultant's job has fundamentally changed. They're not
            starting from a blank whiteboard. They're not spending their first
            three weeks just trying to understand how the client's business
            works. They're starting from a structured, evidence-based
            requirements set that already reflects what the company actually
            does, reconstructed from the company's own documents. The
            consultant's role shifts from discovery to diagnosis.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1027">
            They're reading the requirements and asking a different, far more
            valuable set of questions. Not "how does your receiving process
            work?" — the documents already answered that. Instead: "Is this what
            the company should be doing, or just what they happen to be doing?"
            There's a difference, and it's the difference between a good ERP
            implementation and a system that digitizes existing dysfunction.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1028">
            When a consultant spots something off — a receiving process that
            skips a critical quality inspection step, a procurement flow with no
            approval threshold, an inventory method that works at current volume
            but will collapse at twice the scale — they step in and correct the
            requirements directly. They don't rewrite them from scratch. They
            edit, annotate, and augment what the system produced.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1029">
            Sometimes the correction is straightforward: the AI misread a
            spreadsheet column and inferred the wrong handoff point between
            departments. Sometimes it's profound: the company's actual process,
            faithfully reconstructed from their own documents, reveals a gap
            they didn't know they had. A single point of failure that's been
            masked for years by one experienced employee who catches errors
            manually. A workaround that became permanent policy without anyone
            deciding it should be. The consultant sees these things because
            they've seen them before, at other companies, in other
            implementations. They add the correct requirements — the ones that
            reflect not just how the business runs today, but how it needs to
            run.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1030">
            This is the part that no pure-software play can replicate and no
            traditional consulting model can match. The AI does the work of
            weeks in hours. The consultant does the work that only a human with
            domain expertise can do, but from a position of deep,
            document-verified understanding rather than a blank page. The
            consultant isn't gathering information — they're refining
            intelligence. In fancy terms — machine-scale ingestion with
            human-caliber discernment.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-1031">
            Configuration.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1032">
            The finalized, consultant-reviewed requirements feed directly into
            Thruline's low-code configuration engine. Every configuration
            element — every field, every workflow, every automation — traces
            back to a specific requirement. Whether that requirement was
            generated by the system from source documents or added by a
            consultant who identified a gap, the provenance is preserved. The
            system builds itself from the spec, and the spec is fully traced.
          </p>

          <h3 className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-1033">
            Documentation and Training.
          </h3>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1034">
            Because we know why every configuration exists — including the ones
            the consultant corrected or added — the system generates
            documentation and training materials that explain not just how the
            system works but why it works this way. Not generic help text. Not a
            user manual that describes buttons and menus. Specific,
            contextualized documentation that says: here's how your receiving
            process works in this system, here's the business requirement that
            drove this configuration, and here's what your team needs to know to
            operate it correctly.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1035">
            When a consultant added a quality inspection step that wasn't
            reflected in the original documents, the documentation captures the
            reasoning. Thruline doesn't just record the ERP configuration, it
            records the thinking behind it.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1036">
            The deliverable isn't a configured system. It's a configured system
            plus the organizational knowledge layer that explains it. Our
            customers don't just get an ERP. They get an ERP that can teach
            every employee how their operations work and why.
          </p>
          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1037">
            Thruline's target: 24 hours from documents to a live, traced,
            self-documenting ERP. We're not there today, but the architecture is
            built for it, and we're closing the gap with every implementation.
            Traditional ERP projects take months and cost hundreds of thousands
            of dollars. Ours take days, trending toward one.
          </p>

          <hr className="border-white/10 my-16" data-id="element-1038" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-1039">
            Why Physical Industries Need an ERP Factory First
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1040">
            Tech-forward companies — SaaS, fintech, e-commerce — have relatively
            straightforward ERP needs. Revenue recognition, subscription
            billing, basic accounting, a few dozen workflows, most of them
            digital end-to-end. Their users are tech-literate and can self-serve
            during onboarding, participate meaningfully in structured discovery
            sessions, and read documentation written for a technical audience.
            When something goes wrong, they can Google it. ERP startups like
            Campfire, Dual Entry, and Rillet are serving these companies well,
            and more power to them. That market is being handled.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1041">
            Physical industries are a fundamentally different problem — and the
            difference isn't just cultural. It's structural.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1042">
            First, the operational complexity is orders of magnitude higher. A
            SaaS company's ERP needs to track revenue, expenses, and maybe some
            procurement. A mid-market manufacturer needs to manage purchasing
            across dozens of vendors with different lead times and payment
            terms, inbound receiving with quality inspections that vary by
            material type, warehouse operations with lot tracking and shelf-life
            constraints, production planning that balances machine capacity
            against customer demand, work orders that cascade through multiple
            stages of assembly, outbound shipping with carrier-specific
            compliance requirements, and all of the accounting that ties it
            together. Every one of these processes has exceptions, edge cases,
            and interdependencies with every other process. A change to how you
            receive raw materials ripples into inventory valuation, production
            scheduling, and cost accounting. The web of operational logic in a
            physical business is dense, interconnected, and unforgiving of
            errors in ways that a digital-first company simply never encounters.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1043">
            Second — the people who manage this complexity are not tech-forward.
            They are domain experts, not technology experts. The plant manager
            who runs a flawless receiving operation across three docks and forty
            vendors doesn't think in terms of business requirements and process
            flows. He thinks in terms of the PO that came in this morning, the
            inspection checklist he fills out when raw materials arrive, and the
            spreadsheet he updates before releasing inventory to production. He
            is extraordinarily good at his job, but his expertise lives in the
            doing, not in the describing — and certainly not in describing it in
            the formalized language that ERP implementations demand.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1044">
            This is the core problem. You have the highest operational
            complexity in the enterprise world being managed by people who need
            the most help translating that complexity into a system — and the
            traditional ERP implementation model asks those exact people to do
            the translation themselves. It asks the plant manager to stand at a
            whiteboard and verify a flowchart. It asks the warehouse lead to sit
            through a discovery session and articulate, in structured terms, a
            process she executes instinctively. What comes out is always a
            simplified, sanitized version that misses half the operational
            reality. Not because these people are withholding information —
            because whiteboards and flowcharts aren't how they think about their
            work.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1045">
            This gap — high complexity, low tech literacy — is why physical
            industries require a dramatically higher degree of consultant
            involvement than tech companies. That's why traditional
            implementations in these industries take so long and cost so much.
            The consultant isn't just configuring a system, they're spending
            months serving as a translator between the operational reality on
            the floor and the technical abstraction in the software. Every hour
            of that translation work is billed, and most of it is spent just
            understanding the business before any real configuration begins.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1046">
            The ERP Factory breaks this dynamic by removing the translation step
            entirely. You don't ask the warehouse manager to describe receiving
            in ERP terms. You look at the actual documents that flow through
            receiving — the vendor notifications, the POs, the inspection forms,
            the goods receipt entries — and let the system reconstruct the
            process from the evidence. You meet the company in the language it
            already speaks. The complexity is still there, but it's being read
            from primary sources at machine scale rather than extracted through
            painstaking human interviews.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1047">
            And when the system's reconstruction needs correction, when the
            documents reveal what is happening but not what should be happening,
            the consultant steps in. But now the consultant is operating at the
            level of their actual expertise: diagnosing process gaps,
            prescribing best practices, catching the vulnerabilities that only
            experience can see. They're not burning weeks as a translator,
            they're spending hours as a diagnostician. The factory handles the
            complexity while the consultant handles the judgment.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1048">
            This is why at Thruline we're building the ERP Factory for physical
            industries first. It's not that tech companies don't need faster
            implementation, it's that physical industries can't get properly
            implemented at all without it. The combination of high operational
            complexity and low tech literacy means the traditional model is
            structurally broken for them — too slow, too expensive, and too
            dependent on a translation process that loses critical information
            at every step.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1049">
            This is also why the current crop of "AI-native ERP" startups
            largely miss the mark for these markets. Making an end user more
            productive inside a system they don't understand doesn't fix the
            fundamental problem. It's like giving someone a faster car when they
            don't have a map. You can't automate what the organization doesn't
            understand about itself. You first have to build the understanding —
            from the evidence the organization already produces — and then
            deliver a system that embodies and communicates that understanding
            back to the people who need it. That's the factory's job.
          </p>

          <hr className="border-white/10 my-16" data-id="element-1050" />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-1051">
            Where This Goes
          </h2>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1052">
            The ERP industry has been handcrafting one-off systems for forty
            years. The ERP Factory era starts now.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1053">
            The principles are the same ones driving the Software Factory
            movement in development: machine ingestion of primary sources,
            traceable production, expert quality control, automated
            documentation, compounding knowledge. The structural logic is
            identical. Replace hand-coded software with specs, scenarios, and
            feedback loops. We're replacing hand-gathered requirements with
            document ingestion, consultant quality control, and traced
            configuration. Both moves share the same insight: when you can read
            the primary sources at machine scale, you can manufacture what used
            to require artisan labor.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1054">
            The difference is the stakes. When a software factory produces code,
            the worst case is a bug to fix. When an ERP factory produces a
            system of record for a physical business, it's shaping how an entire
            organization understands and runs its own operations. When the
            documentation that ships with the system explains not just what
            buttons to press but why the process works this way and what happens
            if you change it, you're delivering something no ERP vendor or
            consulting firm has ever delivered: organizational self-awareness as
            a product.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1055">
            That's why the human-in-the-loop — the consultant as quality
            inspector — isn't a compromise or a temporary concession to the
            limits of AI. It's the design. The factory produces the system. The
            consultant ensures the system is right. The ERP configuration
            ensures the system can explain itself. The documentation ensures the
            organization can understand itself.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1056">
            Your business already knows how it works. The evidence is in every
            email, every spreadsheet, every PDF your team produces in the course
            of doing their jobs. The problem has never been that the knowledge
            doesn't exist. The problem is that no one has been reading it.
          </p>

          <p className="text-white/70 text-lg leading-relaxed" data-id="element-1057">
            The ERP Factory reads it. And then it builds you a system that reads
            it back to you — configured, traced, documented, and ready to evolve
            as your business does.
          </p>

          <p className="text-white font-medium text-xl leading-relaxed mt-8" data-id="element-1058">
            At Thruline, this is what we're building. Not a better ERP. The
            first ERP factory.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-24 p-8 md:p-12 rounded-2xl bg-gradient-to-br from-violet-electric/10 to-transparent border border-white/10 text-center" data-id="element-1059">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-1060">
            Ready to build your ERP factory?
          </h3>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto" data-id="element-1061">
            If you're running operations in a physical industry and your ERP
            implementation experience has been anything like what I've described
            — the months of discovery, the documentation that goes stale, the
            system no one fully understands — we'd like to talk.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-navy font-semibold text-base hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1062">

            Book a Demo
            <ArrowRightIcon className="w-4 h-4" data-id="element-1063" />
          </a>
        </div>
      </article>
    </div>;
}